/**
 * Family Finance PWA - React App
 * src/App.jsx
 *
 * Main application component with:
 * - Firebase Authentication
 * - Multi-page navigation via drawer
 * - Real-time sync with Firestore
 */

import React, { useState, useEffect, useRef, Component } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, getDocs, collection, doc } from 'firebase/firestore';
import { Menu, Moon, Sun } from 'lucide-react';
import { logActivity } from './utils/auditLog';

import './App.css';
import Navigation from './components/Navigation';
import LoginPage from './components/LoginPage';
import UpdateBanner from './components/UpdateBanner';
import Toast from './components/Toast';

import Dashboard from './pages/Dashboard';
import Pagos from './pages/Pagos';
import Patrimonio from './pages/Patrimonio';
import Pasivos from './pages/Pasivos';
import Ingresos from './pages/Ingresos';
import Gastos from './pages/Gastos';
import Proyeccion from './pages/Proyeccion';
import Fondos from './pages/Fondos';
import Vacaciones from './pages/Vacaciones';
import Metas from './pages/Metas';
import Catalogos from './pages/Catalogos';
import Historial from './pages/Historial';
import Settings from './pages/Settings';
import AmexEstados from './pages/AmexEstados';
import { SettingsProvider } from './context/SettingsContext';

// Error boundary prevents any page crash from showing a black screen
class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err) { console.error('Page crash:', err); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
          <div style={{ fontWeight: 700, color: '#ef4444', marginBottom: '0.5rem' }}>Error al cargar la página</div>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>{this.state.error.message}</div>
          <button onClick={() => this.setState({ error: null })}
            style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Firebase Configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app, 'myfinances');

const PAGE_LABELS = {
  dashboard: 'Dashboard',
  pagos: 'Pagos',
  patrimonio: 'Patrimonio',
  pasivos: 'Pasivos',
  ingresos: 'Ingresos',
  gastos: 'Gastos',
  proyeccion: 'Proyección',
  fondos: 'Fondos',
  vacaciones: 'Vacaciones',
  metas: 'Metas',
  catalogos: 'Catálogos',
  historial: 'Historial',
  amex: 'AMEX Estados',
  settings: 'Configuración',
};

const APP_VERSION = 'Ver 1.2.0';
const APP_DATE = 'Mar 2026';

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('Admin');
  const [userPermissions, setUserPermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const userRef = useRef(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [swReg, setSwReg] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const handler = (e) => { setSwReg(e.detail); setUpdateReady(true); };
    window.addEventListener('swUpdateAvailable', handler);
    return () => window.removeEventListener('swUpdateAvailable', handler);
  }, []);

  const handleUpdate = () => {
    if (swReg && swReg.waiting) {
      swReg.waiting.postMessage('SKIP_WAITING');
      swReg.waiting.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') window.location.reload();
      });
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Check if user is registered in the users collection
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          if (usersSnap.empty) {
            // No users registered yet — first run, allow in
            setAccessDenied(false);
          } else {
            const userDoc = usersSnap.docs.find(d => d.id === currentUser.email);
            if (!userDoc) {
              // User not in registry — deny access
              await signOut(auth);
              setAccessDenied(true);
              setLoading(false);
              return;
            }
            setAccessDenied(false);
            setUserRole(userDoc.data().role || 'Admin');
            setUserPermissions(userDoc.data().permissions || null);
          }
        } catch {
          // If rules check fails, allow (fallback for initial setup)
          setAccessDenied(false);
        }

        if (!userRef.current) {
          logActivity(db, currentUser, 'crear', 'Sesión', { accion: 'Inicio de sesión', email: currentUser.email });
        }
      } else {
        setAccessDenied(false);
      }
      userRef.current = currentUser;
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="app-loading" style={{ flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontSize: '2rem' }}>🔒</div>
        <p style={{ fontWeight: 700, color: '#ef4444', fontSize: '1.1rem' }}>Acceso denegado</p>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', maxWidth: '320px' }}>
          Tu cuenta no está registrada en el sistema. Contacta al administrador para solicitar acceso.
        </p>
        <button onClick={() => { setAccessDenied(false); signOut(auth); }}
          style={{ padding: '0.6rem 1.5rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', fontWeight: 600, cursor: 'pointer', marginTop: '0.5rem' }}>
          Volver al inicio
        </button>
      </div>
    );
  }

  if (!user) {
    return <LoginPage auth={auth} />;
  }

  const renderPage = () => {
    const props = { db, user };
    switch (currentPage) {
      case 'dashboard':   return <Dashboard {...props} />;
      case 'pagos':       return <Pagos {...props} />;
      case 'patrimonio':  return <Patrimonio {...props} />;
      case 'pasivos':     return <Pasivos {...props} />;
      case 'ingresos':    return <Ingresos {...props} />;
      case 'gastos':      return <Gastos {...props} />;
      case 'proyeccion':  return <Proyeccion {...props} />;
      case 'fondos':      return <Fondos {...props} />;
      case 'vacaciones':  return <Vacaciones {...props} />;
      case 'metas':       return <Metas {...props} />;
      case 'catalogos':   return <Catalogos {...props} />;
      case 'historial':   return <Historial {...props} />;
      case 'amex':        return <AmexEstados {...props} />;
      case 'settings':    return <Settings db={db} user={user} />;
      default:            return <Dashboard {...props} />;
    }
  };

  return (
    <SettingsProvider db={db}>
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="btn-hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu size={24} />
            </button>
            <span className="header-page-name">{PAGE_LABELS[currentPage] || 'Dashboard'}</span>
          </div>
          <div className="header-right">
            <span className="header-email">{user.displayName || user.email}</span>
            <div className="user-avatar" title={user.displayName || user.email}>
              {(user.displayName || user.email).charAt(0).toUpperCase()}
            </div>
            <button
              className="btn-theme-toggle"
              onClick={() => setDarkMode(d => !d)}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
              aria-label="Cambiar tema"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="btn-logout" onClick={handleSignOut} title="Cerrar sesión">
              Salir
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Drawer */}
      <Navigation
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        user={user}
        userRole={userRole}
        userPermissions={userPermissions}
      />

      {/* Page Content */}
      <main className="app-main">
        <PageErrorBoundary key={currentPage}>
          {renderPage()}
        </PageErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="app-footer-brand">
        <span className="app-footer-version">{APP_VERSION} · {APP_DATE}</span>
        <span className="app-footer-sep">·</span>
        <span className="app-footer-powered">Powered by</span>
        <img
          src={darkMode ? '/cipheria-white.svg' : '/cipheria-color.svg'}
          alt="Cipheria"
          className="app-footer-logo"
        />
      </footer>

      {updateReady && <UpdateBanner onUpdate={handleUpdate} />}
      <Toast />
    </div>
    </SettingsProvider>
  );
}
